import React, {
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  MessageInput,
  MessageInputProps,
  MessageList,
  MessageListProps,
  useChannelActionContext,
  useChannelStateContext,
  useMessageComposer,
} from 'stream-chat-react';
import { z } from 'zod';

import { ErrorText } from 'src/components/ErrorText';
import { useAssistantContext } from 'src/context';
import { ChannelStatusIndicators } from 'src/features/channel/channel-content/ChannelStatusIndicators';
import { useOSDetector } from 'src/hooks/useOSDetector';
import { usePrefill } from 'src/hooks/usePrefill';
// TODO in POR-15598: move MessageContent out of the legacy directory
// eslint-disable-next-line no-restricted-imports
import { MessageContent } from 'src/legacy/components/Chat/MessageContent';
import { AssistantUserID } from 'src/store/clients/types';
import { useCopyHandler } from 'src/utils/ClipboardUtils';

export const MAX_CHARACTERS_LIMIT = 5000;
export const MAX_CHARACTERS_LIMIT_ERROR =
  'You have reached the 5000 character limit';

interface MessageListComponentProps extends MessageListProps {
  suppressAutoScroll?: boolean;
}
const MessageListComponent = (props: MessageListComponentProps) => {
  return <MessageList {...props} />;
};

// This is wrapper around MessageInput to allow for the textareaRef to be passed in.
// The MessageInputProps from stream-chat-react does not include textareaRef.
// but it does accept the prop. So we need to create a wrapper to allow for it
// to be passed in.
interface MessageInputComponentProps extends MessageInputProps {
  textareaRef?: MutableRefObject<HTMLTextAreaElement | undefined>;
}
const MessageInputComponent = (props: MessageInputComponentProps) => {
  return (
    <div className="z-10">
      <MessageInput {...props} />
    </div>
  );
};

const prefillDataSchema = z
  .object({
    body: z.string(),
  })
  .passthrough();

const usePrefilledMessageDraft = ({
  isInternalChat,
  messageInputRef,
}: {
  isInternalChat: boolean;
  messageInputRef: MutableRefObject<HTMLTextAreaElement | undefined>;
}) => {
  const {
    setTextareaRef,
    setTextComposer,
    setMessageDraft,
    isSetMessageDraftReady,
  } = useAssistantContext();
  const { prefilledData, action } = usePrefill(
    prefillDataSchema,
    'sendMessage',
  );
  const prefillAppliedRef = useRef(false);
  const { textComposer } = useMessageComposer();

  useEffect(() => {
    if (!isInternalChat) {
      setTextareaRef(messageInputRef);
      setTextComposer(textComposer);
    }
    return () => {
      setTextareaRef(null);
      setTextComposer(null);
    };
  }, [
    setTextareaRef,
    setTextComposer,
    textComposer,
    isInternalChat,
    messageInputRef,
  ]);

  // Reset prefill flag when prefilled data changes
  useEffect(() => {
    prefillAppliedRef.current = false;
  }, [prefilledData?.body]);

  // Handle prefill using setMessageDraft from assistantContext
  useEffect(() => {
    if (
      !isInternalChat &&
      isSetMessageDraftReady &&
      prefilledData?.body &&
      !prefillAppliedRef.current &&
      action === 'sendMessage'
    ) {
      prefillAppliedRef.current = true;
      setMessageDraft(prefilledData.body);
    }
  }, [
    isInternalChat,
    prefilledData?.body,
    setMessageDraft,
    isSetMessageDraftReady,
    action,
  ]);
};

export const ChannelContent = ({
  ChannelToolbar,
  MessageListOverride,
  autoFocus = true,
  defaultInputValue = '',
  onChange,
  onSubmit,
  showMessageInput = true,
  placeholderText,
  messageId,
  aiAssistantStatus,
  typingStatus,
  isInternalChat = false,
  setAIAssistantStatus,
  clearAIAssistantStatus,
  cancelAIStream,
  getCompanyFromId,
  getClientFromId,
  allowScroll = false,
}: {
  ChannelToolbar?: ReactNode;
  MessageListOverride?: ReactNode;
  autoFocus?: boolean;
  defaultInputValue?: string;
  onChange?: (text: string) => void;
  onSubmit?: () => void;
  showMessageInput?: boolean;
  placeholderText?: string;
  messageId?: string;
  aiAssistantStatus?: {
    status: string;
    metadata?: {
      tool?: string;
      query?: string;
    };
    messageId?: string;
    isCancelable: boolean;
  } | null;
  typingStatus?: {
    typingUsers: Array<{
      user?: {
        id?: string;
        name?: string;
      };
    }>;
  } | null;
  isInternalChat?: boolean;
  setAIAssistantStatus?: (
    channelId: string,
    status: string,
    metadata?: { tool?: string; query?: string },
    messageId?: string,
  ) => void;
  clearAIAssistantStatus?: () => void;
  cancelAIStream?: () => void;
  getCompanyFromId?: (companyId: string) => { name?: string } | null;
  getClientFromId?: (clientId: string) => { name?: string } | null;
  allowScroll?: boolean;
}) => {
  const isFirstRender = useRef(true);
  const [hasMaxLimitReached, setHasMaxLimitReached] = useState(false);

  const messageComposer = useMessageComposer();
  const { channel } = useChannelStateContext();

  const messageInputRef = useRef<HTMLTextAreaElement>();
  usePrefilledMessageDraft({ isInternalChat, messageInputRef });

  const { sendMessage, jumpToMessage } = useChannelActionContext();
  useEffect(() => {
    if (messageId && jumpToMessage) {
      void jumpToMessage(messageId);
    }
  }, [messageId, jumpToMessage]);

  /**
   * This keyboard event listener handles focus the chat input
   * when ctrl & / keys are pressed.
   * @param event
   */
  const { isOSMetaKeyPressed } = useOSDetector();
  const onKeyDownHandler = (event: KeyboardEvent) => {
    if (isOSMetaKeyPressed(event)) {
      if (event.key === '/') {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }
    }
  };

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDownHandler, false);
    return () => {
      window.removeEventListener('keydown', onKeyDownHandler, false);
    };
  }, []);

  // register onChange handler for the message composer
  useEffect(() => {
    if (onChange) {
      messageComposer.textComposer.middlewareExecutor.use([
        {
          id: 'input-state-handler',
          handlers: {
            onChange: async ({ state, next }) => {
              onChange?.(state.text);
              return next(state);
            },
            // we don't need to handle the suggestion item select
            // but since this is a required prop we need to define it
            // in the middleware executor
            onSuggestionItemSelect: async ({ state, next }) => {
              return next(state);
            },
          },
        },
      ]);
    }

    return () => {
      messageComposer.textComposer.middlewareExecutor.remove(
        'input-state-handler',
      );
    };
  }, [onChange, messageComposer.textComposer.middlewareExecutor]);

  const defaultInputValueRef = useRef(defaultInputValue);
  // Update textarea value when inputValue changes
  useEffect(() => {
    if (defaultInputValueRef.current && messageComposer.textComposer) {
      // Set the value and trigger both input and change events
      messageComposer.textComposer.setText(defaultInputValueRef.current);

      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }
  }, [messageComposer.textComposer]);

  /**
   * This method handles the chat input key up event.
   * 1. It checks if the message input has reached the max character limit.
   * 2. It allows the user to blur the input by pressing the escape key.
   */
  const handleInputKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.currentTarget.value.length >= MAX_CHARACTERS_LIMIT) {
        setHasMaxLimitReached(true);
      } else {
        setHasMaxLimitReached(false);
      }

      if (event.key === 'Escape') {
        messageInputRef.current?.blur();
      }
    },
    [],
  );

  const handleCopyFromMessageList = useCopyHandler({
    skipTargetElements: ['textarea', 'input'],
  });

  return (
    <>
      {ChannelToolbar}
      <div
        onCopy={handleCopyFromMessageList}
        className={`flex-1 ${allowScroll ? 'overflow-x-hidden' : 'overflow-hidden'}`}
      >
        {MessageListOverride ? (
          MessageListOverride
        ) : (
          <MessageListComponent
            Message={MessageContent}
            suppressAutoScroll={false}
            scrolledUpThreshold={50}
          />
        )}
      </div>
      {/* Unified status indicators: AI assistant status and typing indicators */}
      <ChannelStatusIndicators
        aiAssistantStatus={aiAssistantStatus}
        typingStatus={typingStatus}
        isInternalChat={isInternalChat}
        getCompanyFromId={getCompanyFromId}
        getClientFromId={getClientFromId}
        clearAIAssistantStatus={clearAIAssistantStatus}
        cancelAIStream={cancelAIStream}
      />
      {showMessageInput && (
        <>
          {hasMaxLimitReached && (
            <div className="px-4">
              <ErrorText>{MAX_CHARACTERS_LIMIT_ERROR}</ErrorText>
            </div>
          )}
          <MessageInputComponent
            // use textAreaRef prop to reference the chatAutoComplete component
            // passing the ref to the additionalTextAreaProp object causes
            // submitting message with enter key to not work.
            textareaRef={messageInputRef}
            maxRows={10}
            // focus input prop does not work when passing ref to the textarea
            // using textareaRef prop. This is a workaround to make it work.
            additionalTextareaProps={{
              autoFocus: isFirstRender.current && autoFocus,
              onKeyUp: handleInputKeyUp,
              // stream chat max character limit is 5000 by default
              // Here we are making sure is not able to type/paste more than 5000 characters in the input
              maxLength: MAX_CHARACTERS_LIMIT,
              placeholder: placeholderText,
            }}
            overrideSubmitHandler={async (params) => {
              if (onSubmit) {
                onSubmit();
              }

              // Set optimistic "sending" status for AI assistant only when AI is mentioned
              const channelId = channel?.cid;
              const isAIMentioned =
                params.localMessage?.mentioned_users?.some(
                  (user) => user.id === AssistantUserID,
                ) || false;

              // reset hasMaxLimitReached state
              setHasMaxLimitReached(false);
              try {
                if (isAIMentioned) {
                  const message = {
                    ...params.message,
                    user_timezone:
                      Intl.DateTimeFormat().resolvedOptions().timeZone,
                  };
                  params.message = message;
                }

                await sendMessage(params);

                if (
                  channelId &&
                  isInternalChat &&
                  isAIMentioned &&
                  setAIAssistantStatus
                ) {
                  // Set optimistic status without message ID - backend will "claim" it later
                  setAIAssistantStatus(channelId, 'sending', undefined);
                }
              } catch (error) {
                if (isInternalChat && isAIMentioned && clearAIAssistantStatus) {
                  clearAIAssistantStatus();
                }
              }
            }}
          />
        </>
      )}
    </>
  );
};
